/**
 * @author: Tyler Schwegler
 * @date: 24/07/2024
 *
 * Coordinates Formats
 *
 * Degrees, Minutes, and Seconds (DMS)
 *
 * Latitude: Degrees (0 to 90) + Minutes (0 to 59) + Seconds (0 to 59.9999)
 * Longitude: Degrees (0 to 180) + Minutes (0 to 59) + Seconds (0 to 59.9999)
 * Direction: N/S for latitude and E/W for longitude
 * Regex: /^(\d{1,3})°( )?(\d{1,2})'?(\d{1,2}(\.\d+)?)"[NS] (\d{1,3})°( )?(\d{1,2})'?(\d{1,2}(\.\d+)?)"[EW]$/
 * Example: 41°24'12.2"N 2°10'26.5"E
 *
 * Degrees and Decimal Minutes (DDM)
 *
 * Latitude: Degrees (0 to 90) + Decimal Minutes (0 to 59.9999)
 * Longitude: Degrees (0 to 180) + Decimal Minutes (0 to 59.9999)
 * Direction: N/S for latitude and E/W for longitude
 * Regex: /^(\d{1,3}) (\d{1,2}(\.\d+)?)([ NS])?, (\d{1,3}) (\d{1,2}(\.\d+)?)([ EW])?$/
 * Example: 41 24.2028, 2 10.4418
 *
 * Decimal Degrees (DD)
 *
 * Latitude: Degrees (0 to 90) + Decimal Fractions
 * Longitude: Degrees (0 to 180) + Decimal Fractions
 * Positive for N latitude and E longitude, negative for S latitude and W longitude
 * Regex: /^(-?\d+(\.\d+)?)( )?([NS])?, (-?\d+(\.\d+)?)?( )?([EW])?$
 * Example: 41.40338, 2.17403
 */

const ddPattern = /^(-?\d+(\.\d+)?)( )?([NS])?, (-?\d+(\.\d+)?)?( )?([EW])?$/;
const dmsPattern = /^(\d{1,3})°( )?(\d{1,2})'?(\d{1,2}(\.\d+)?)"[NS] (\d{1,3})°( )?(\d{1,2})'?(\d{1,2}(\.\d+)?)"[EW]$/;
const ddmPattern = /^(\d{1,3}) (\d{1,2}(\.\d+)?)([ NS])?, (\d{1,3}) (\d{1,2}(\.\d+)?)([ EW])?$/;

export const validateGPS = (coordinates) => {
  const ddMatch = ddPattern.test(coordinates) && isValidDD(coordinates);
  const dmsMatch = dmsPattern.test(coordinates) && isValidDMS(coordinates);
  const ddmMatch = ddmPattern.test(coordinates) && isValidDDM(coordinates);

  return ddMatch || dmsMatch || ddmMatch;
};

const isValidDD = (coordinate) => {
  const parts = coordinate.match(ddPattern);
  if (!parts) return false;
  const lat = parseFloat(parts[1]);
  const lon = parseFloat(parts[5]);

  return lat >= -90 && lat <= 90 && lon >= -180 && lon <= 180;
};

const isValidDMS = (coordinate) => {
  const parts = coordinate.match(dmsPattern);
  if (!parts) return false;
  const latDeg = parseInt(parts[1]);
  const latMin = parseInt(parts[3]);
  const latSec = parseFloat(parts[4]);
  const lonDeg = parseInt(parts[6]);
  const lonMin = parseInt(parts[8]);
  const lonSec = parseFloat(parts[9]);

  return (
    latDeg >= 0 &&
    latDeg <= 90 &&
    latMin >= 0 &&
    latMin < 60 &&
    latSec >= 0 &&
    latSec < 60 &&
    lonDeg >= 0 &&
    lonDeg <= 180 &&
    lonMin >= 0 &&
    lonMin < 60 &&
    lonSec >= 0 &&
    lonSec < 60
  );
};

const isValidDDM = (coordinate) => {
  const parts = coordinate.match(ddmPattern);
  if (!parts) return false;
  const latDeg = parseInt(parts[1]);
  const latMin = parseFloat(parts[2]);
  const lonDeg = parseInt(parts[5]);
  const lonMin = parseFloat(parts[6]);

  return (
    latDeg >= 0 &&
    latDeg <= 90 &&
    latMin >= 0 &&
    latMin < 60 &&
    lonDeg >= 0 &&
    lonDeg <= 180 &&
    lonMin >= 0 &&
    lonMin < 60
  );
};
